"use client";

/**
 * #331 — "Editar dados do posto" dialog.
 *
 * Allows post-creation editing of levantamento metadata so operators can
 * supply latitude/longitude (required by the CSV HARD gate) and other fields
 * without having to re-create the levantamento.
 *
 * Intentionally self-contained: reuses shadcn primitives and copies the
 * numeric-validation logic from the create dialog (ponytail: small duplication
 * is preferable to refactoring the tested create dialog for marginal gain).
 */

import { useState } from "react";
import { toast } from "sonner";

import { usePatchLevantamento } from "@/lib/api-video";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// ---------------------------------------------------------------------------
// Local helpers (mirror of create dialog — ponytail: kept local intentionally)
// ---------------------------------------------------------------------------

const inputCls =
  "rounded-md border border-tide-border bg-tide-bg-hover px-2.5 py-1.5 text-sm text-tide-text-primary outline-none focus:border-tide-accent w-full";

function FormField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium text-tide-text-secondary">
        {label}
      </label>
      {children}
    </div>
  );
}

const SENTIDO_OPTIONS = ["NS", "SN", "LO", "OL", "Único"] as const;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface EditLevantamentoDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  levantamento: {
    id: string;
    posto_contagem: string;
    rodovia: string | null;
    km: string | null;
    latitude: string | null;
    longitude: string | null;
    sentido: string;
    movimento: string | null;
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EditLevantamentoDialog({
  open,
  onOpenChange,
  levantamento,
}: EditLevantamentoDialogProps) {
  const patchMut = usePatchLevantamento(levantamento.id);

  // ponytail: state initializes from props on mount; parent mounts this dialog
  // only while open (remounts per open) so no reset effect is needed.
  const [posto, setPosto] = useState(levantamento.posto_contagem);
  const [rodovia, setRodovia] = useState(levantamento.rodovia ?? "");
  const [sentido, setSentido] = useState(levantamento.sentido);
  const [movimento, setMovimento] = useState(levantamento.movimento ?? "");
  const [km, setKm] = useState(levantamento.km ?? "");
  const [latitude, setLatitude] = useState(levantamento.latitude ?? "");
  const [longitude, setLongitude] = useState(levantamento.longitude ?? "");

  const [formError, setFormError] = useState<string | null>(null);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);

    // posto required
    if (!posto.trim()) {
      setFormError("Posto de contagem é obrigatório.");
      return;
    }

    // lat/lng optional but must be numeric + in range when provided
    const latTrimmed = latitude.trim();
    const lngTrimmed = longitude.trim();

    if (latTrimmed) {
      const latNum = Number(latTrimmed);
      if (isNaN(latNum) || latNum < -90 || latNum > 90) {
        setFormError("Latitude deve ser um número entre -90 e 90.");
        return;
      }
    }
    if (lngTrimmed) {
      const lngNum = Number(lngTrimmed);
      if (isNaN(lngNum) || lngNum < -180 || lngNum > 180) {
        setFormError("Longitude deve ser um número entre -180 e 180.");
        return;
      }
    }

    patchMut.mutate(
      {
        posto_contagem: posto.trim(),
        rodovia: rodovia.trim() || null,
        sentido,
        movimento: movimento.trim() || null,
        km: km.trim() || null,
        latitude: latTrimmed || null,
        longitude: lngTrimmed || null,
      },
      {
        onSuccess: () => {
          toast.success("Dados do posto atualizados.");
          onOpenChange(false);
        },
        onError: (err) => {
          setFormError(
            (err as Error)?.message ?? "Erro ao atualizar dados do posto.",
          );
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="edit-levantamento-dialog">
        <DialogHeader>
          <DialogTitle>Editar dados do posto</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4 text-sm">
          {/* Posto */}
          <FormField label="Posto de contagem *">
            <input
              className={inputCls}
              value={posto}
              onChange={(e) => setPosto(e.target.value)}
              placeholder="ex. BR-381 km 142 Norte"
              data-testid="edit-field-posto"
              required
            />
          </FormField>

          {/* Rodovia */}
          <FormField label="Rodovia">
            <input
              className={inputCls}
              value={rodovia}
              onChange={(e) => setRodovia(e.target.value)}
              placeholder="ex. BR-381"
              data-testid="edit-field-rodovia"
            />
          </FormField>

          {/* Sentido */}
          <FormField label="Sentido *">
            <select
              className={inputCls}
              value={sentido}
              onChange={(e) => setSentido(e.target.value)}
              data-testid="edit-field-sentido"
            >
              {SENTIDO_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </FormField>

          {/* Movimento */}
          <FormField label="Movimento">
            <input
              className={inputCls}
              value={movimento}
              onChange={(e) => setMovimento(e.target.value)}
              placeholder="ex. TMC"
              data-testid="edit-field-movimento"
            />
          </FormField>

          {/* KM */}
          <FormField label="KM">
            <input
              type="number"
              step="0.001"
              className={inputCls}
              value={km}
              onChange={(e) => setKm(e.target.value)}
              placeholder="ex. 142.5"
              data-testid="edit-field-km"
            />
          </FormField>

          {/* Lat / Lng */}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Latitude">
              <input
                type="number"
                step="0.000001"
                min={-90}
                max={90}
                className={inputCls}
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                placeholder="-19.912"
                data-testid="edit-field-latitude"
              />
            </FormField>
            <FormField label="Longitude">
              <input
                type="number"
                step="0.000001"
                min={-180}
                max={180}
                className={inputCls}
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                placeholder="-43.940"
                data-testid="edit-field-longitude"
              />
            </FormField>
          </div>

          {formError && (
            <p className="text-xs text-red-400" role="alert">
              {formError}
            </p>
          )}
        </form>

        <DialogFooter>
          <Button
            type="submit"
            data-testid="btn-save-metadata"
            disabled={patchMut.isPending}
            onClick={(e) => {
              const form = (e.currentTarget as HTMLElement)
                .closest("[data-slot='dialog-content']")
                ?.querySelector("form");
              form?.requestSubmit();
            }}
          >
            {patchMut.isPending ? "Salvando…" : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
